import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api, BACKEND_URL } from '../lib/api';
import { Button } from '../components/ui/button';
import { ArrowLeft, Download, Edit, CheckCircle, XCircle, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

export default function DriverDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [driver, setDriver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    loadDriver();
  }, [id]);

  const loadDriver = async () => {
    try {
      const response = await api.get(`/drivers/${id}`);
      setDriver(response.data);
    } catch (error) {
      console.error('Failed to load driver:', error);
      toast.error('Failed to load driver details');
    } finally {
      setLoading(false);
    }
  };

  const downloadDocument = (url, filename) => {
    if (!url) {
      toast.error('Document not available');
      return;
    }
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleApprove = async () => {
    setActionLoading(true);
    try {
      const { data } = await api.patch(`/drivers/${id}/status`, { status: 'active' });
      setDriver(data);
      toast.success('Driver approved successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve driver');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    setActionLoading(true);
    try {
      const { data } = await api.patch(`/drivers/${id}/status`, {
        status: 'rejected',
        rejection_reason: rejectionReason,
      });
      setDriver(data);
      setShowRejectModal(false);
      setRejectionReason('');
      toast.success('Driver rejected');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject driver');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-shizah-blue"></div>
      </div>
    );
  }

  if (!driver) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-500">Driver not found</p>
        <Button onClick={() => navigate('/drivers')} className="mt-4">
          Back to Drivers
        </Button>
      </div>
    );
  }

  const DocumentCard = ({ title, imageUrl, docNumber, frontUrl, backUrl }) => (
    <div className="bg-gray-50 p-4 rounded-lg">
      <h4 className="font-semibold text-gray-900 mb-3">{title}</h4>
      {docNumber && (
        <p className="text-sm text-gray-600 mb-2">
          <span className="font-medium">Number:</span> {docNumber}
        </p>
      )}
      <div className="space-y-2">
        {imageUrl && (
          <div>
            <img
              src={imageUrl}
              alt={title}
              className="w-full h-48 object-cover rounded border border-gray-300 mb-2"
            />
            <Button
              onClick={() => downloadDocument(imageUrl, `${driver.driver_id}_${title}.jpg`)}
              size="sm"
              variant="outline"
              className="w-full"
              data-testid={`download-${title.toLowerCase().replace(/\\s/g, '-')}`}
            >
              <Download size={16} className="mr-2" />
              Download
            </Button>
          </div>
        )}
        {frontUrl && (
          <div>
            <p className="text-xs text-gray-500 mb-1">Front</p>
            <img
              src={frontUrl}
              alt={`${title} Front`}
              className="w-full h-48 object-cover rounded border border-gray-300 mb-2"
            />
            <Button
              onClick={() => downloadDocument(frontUrl, `${driver.driver_id}_${title}_Front.jpg`)}
              size="sm"
              variant="outline"
              className="w-full"
            >
              <Download size={16} className="mr-2" />
              Download Front
            </Button>
          </div>
        )}
        {backUrl && (
          <div className="mt-2">
            <p className="text-xs text-gray-500 mb-1">Back</p>
            <img
              src={backUrl}
              alt={`${title} Back`}
              className="w-full h-48 object-cover rounded border border-gray-300 mb-2"
            />
            <Button
              onClick={() => downloadDocument(backUrl, `${driver.driver_id}_${title}_Back.jpg`)}
              size="sm"
              variant="outline"
              className="w-full"
            >
              <Download size={16} className="mr-2" />
              Download Back
            </Button>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div data-testid="driver-details-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <Button
            onClick={() => navigate('/drivers')}
            variant="ghost"
            data-testid="back-button"
            className="mb-4"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Drivers
          </Button>
          <h1 className="text-4xl font-black font-heading text-gray-900 mb-2">Driver Details</h1>
          <p className="text-gray-600">Complete profile and documents</p>
        </div>
        <Button
          onClick={() => navigate(`/drivers/${id}/edit`)}
          data-testid="edit-driver-button"
          className="bg-shizah-blue hover:bg-blue-700 text-white"
        >
          <Edit size={20} className="mr-2" />
          Edit Driver
        </Button>
      </div>

      {/* Pending Approval Banner */}
      {driver.status === 'pending_approval' && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-5 mb-6" data-testid="pending-approval-banner">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <AlertTriangle size={24} className="text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-amber-900 text-lg">Pending Approval</h3>
                <p className="text-amber-800 text-sm mt-1">
                  This driver registered via the QR code self-onboarding form. Review their documents and details below, then approve or reject.
                </p>
              </div>
            </div>
            <div className="flex gap-3 flex-shrink-0">
              <Button
                onClick={handleApprove}
                disabled={actionLoading}
                data-testid="approve-driver-button"
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <CheckCircle size={18} className="mr-2" />
                Approve
              </Button>
              <Button
                onClick={() => setShowRejectModal(true)}
                disabled={actionLoading}
                data-testid="reject-driver-button"
                variant="outline"
                className="border-red-500 text-red-600 hover:bg-red-50"
              >
                <XCircle size={18} className="mr-2" />
                Reject
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Rejected Banner */}
      {driver.status === 'rejected' && (
        <div className="bg-red-50 border border-red-300 rounded-lg p-5 mb-6" data-testid="rejected-banner">
          <div className="flex items-start gap-3">
            <XCircle size={24} className="text-red-600 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="font-bold text-red-900 text-lg">Rejected</h3>
              {driver.rejection_reason && (
                <p className="text-red-800 text-sm mt-1">Reason: {driver.rejection_reason}</p>
              )}
              {driver.reviewed_by && (
                <p className="text-red-700 text-xs mt-1">Reviewed by: {driver.reviewed_by}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reject Reason Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="reject-modal">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-3">Reject Driver</h3>
            <p className="text-sm text-gray-600 mb-4">Please provide a reason for rejecting this driver application.</p>
            <textarea
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              placeholder="Enter rejection reason..."
              className="w-full border border-gray-300 rounded-md p-3 text-sm min-h-[100px] focus:outline-none focus:ring-2 focus:ring-red-500"
              data-testid="rejection-reason-input"
            />
            <div className="flex justify-end gap-3 mt-4">
              <Button
                onClick={() => { setShowRejectModal(false); setRejectionReason(''); }}
                variant="ghost"
                data-testid="cancel-reject-button"
              >
                Cancel
              </Button>
              <Button
                onClick={handleReject}
                disabled={actionLoading}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="confirm-reject-button"
              >
                {actionLoading ? 'Rejecting...' : 'Confirm Reject'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Personal Information */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h2 className="text-2xl font-bold font-heading text-gray-900 mb-4">Personal Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div>
            <p className="text-sm text-gray-500">Driver ID</p>
            <p className="font-semibold text-shizah-blue text-lg">{driver.driver_id}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Full Name</p>
            <p className="font-semibold text-gray-900">{driver.full_name}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Mobile Number</p>
            <p className="font-semibold text-gray-900">{driver.mobile}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Location</p>
            <p className="font-semibold text-gray-900">{driver.location}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Status</p>
            <span
              className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                driver.status === 'active'
                  ? 'bg-green-100 text-green-800'
                  : driver.status === 'pending_approval'
                  ? 'bg-yellow-100 text-yellow-800'
                  : driver.status === 'rejected'
                  ? 'bg-red-100 text-red-800'
                  : 'bg-gray-100 text-gray-800'
              }`}
            >
              {driver.status === 'pending_approval' ? 'Pending Approval' : driver.status}
            </span>
          </div>
        </div>
      </div>

      {/* Documents */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h2 className="text-2xl font-bold font-heading text-gray-900 mb-4">Documents</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <DocumentCard
            title="PAN Card"
            docNumber={driver.pan_number}
            imageUrl={driver.pan_image_url}
          />
          <DocumentCard
            title="Aadhar Card"
            docNumber={driver.aadhar_number}
            frontUrl={driver.aadhar_front_url}
            backUrl={driver.aadhar_back_url}
          />
          <DocumentCard
            title="Driving License"
            docNumber={driver.license_number}
            frontUrl={driver.license_front_url}
            backUrl={driver.license_back_url}
          />
          <DocumentCard title="Driver Selfie" imageUrl={driver.selfie_url} />
        </div>
      </div>

      {/* Bank Details */}
      <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h2 className="text-2xl font-bold font-heading text-gray-900 mb-4">Bank Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <p className="text-sm text-gray-500">Account Holder Name</p>
            <p className="font-semibold text-gray-900">{driver.bank_details.account_holder_name}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Account Number</p>
            <p className="font-semibold text-gray-900">{driver.bank_details.account_number}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">IFSC Code</p>
            <p className="font-semibold text-gray-900">{driver.bank_details.ifsc}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Bank Name</p>
            <p className="font-semibold text-gray-900">{driver.bank_details.bank_name}</p>
          </div>
        </div>
      </div>

      {/* References */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h2 className="text-2xl font-bold font-heading text-gray-900 mb-4">References</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-3">Reference 1</h3>
            <div className="space-y-2">
              <div>
                <p className="text-sm text-gray-500">Name</p>
                <p className="font-semibold text-gray-900">{driver.reference1.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Mobile</p>
                <p className="font-semibold text-gray-900">{driver.reference1.mobile}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Relation</p>
                <p className="font-semibold text-gray-900">{driver.reference1.relation}</p>
              </div>
            </div>
          </div>

          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="font-semibold text-gray-900 mb-3">Reference 2</h3>
            <div className="space-y-2">
              <div>
                <p className="text-sm text-gray-500">Name</p>
                <p className="font-semibold text-gray-900">{driver.reference2.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Mobile</p>
                <p className="font-semibold text-gray-900">{driver.reference2.mobile}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Relation</p>
                <p className="font-semibold text-gray-900">{driver.reference2.relation}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
